import React, { useState } from "react";
import { Row, Col, Card, Modal, Button, Spinner } from "reactstrap";
import PropTypes from "prop-types";

import SeshCard from "./SeshCard";
import useDocumentTitle from "../hooks/useDocumentTitle";
import "./Home.scss";
import "bootstrap4-fs-modal/dist/css/bootstrap-fs-modal.min.css";
import {
  useCreateMutation,
  useDeleteMutation,
  useEditMutation,
  useMutationHandler,
} from "../hooks/mutations";
import { useCreateToast } from "../hooks/toasts";
import { useSeshes } from "../hooks/seshes";
import { usePrefetchSpots } from "../hooks/spots";
import NewSeshForm from "./SeshForm/NewSeshForm";
import ConfirmModal from "./ConfirmModal";

const Home = (props) => {
  const [modal, setModal] = useState(false);
  const toggleModal = () => setModal((prev) => !prev);
  const [seshToEdit, setSeshToEdit] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const toggleConfirm = () => setShowConfirm((prev) => !prev);

  const createToast = useCreateToast();
  useDocumentTitle("Home");

  const { api } = props;

  const fillEditModal = (seshId, pageNumber) => {
    setEditMode(true);
    setModal(true);
    setSeshToEdit(seshes.pages[pageNumber].seshes.find((s) => s.id === seshId));
  };

  const deleteSeshClick = (seshId, pageNumber) => {
    setSeshToEdit(seshes.pages[pageNumber].seshes.find((s) => s.id === seshId));
    toggleConfirm();
  };
  const confirmClick = (seshToDelete) => {
    handleDelete(seshToDelete);
    toggleConfirm();
  };
  const {
    data: seshes,
    fetchNextPage,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
  } = useSeshes();

  const renderPage = (page, pageNumber) => {
    if (page.seshes.length === 0) {
      return (
        <Row>
          <Col xs="auto" className="mx-auto">
            <span>No seshes found</span>
          </Col>
        </Row>
      );
    } else {
      return (
        <>
          {page.seshes.map((sesh) => (
            <Row key={sesh.id}>
              <SeshCard
                pageNumber={pageNumber}
                sesh={sesh}
                editSesh={fillEditModal}
                handleDelete={deleteSeshClick}
              />
            </Row>
          ))}
        </>
      );
    }
  };

  const deleteSesh = useDeleteMutation(api.seshes);
  const deleteSuccess = () => {
    createToast("success", "Successfully deleted sesh");
  };
  const deleteError = () => {
    createToast("danger", "Unable to delete sesh. Please try again");
  };
  const handleDelete = useMutationHandler(
    ["seshes"],
    deleteSesh,
    deleteSuccess,
    deleteError
  );
  const createSuccess = () => {
    createToast("success", "Successfully created sesh");
    setModal(false);
  };
  const createError = () => {
    createToast("danger", "Unable to create sesh. Please try again");
  };
  const createSesh = useCreateMutation(api.seshes);
  const handleCreateSesh = useMutationHandler(
    ["seshes"],
    createSesh,
    createSuccess,
    createError
  );
  const editSuccess = () => {
    createToast("success", "Successfully edited sesh");
    setModal(false);
  };
  const editError = () => {
    createToast("danger", "Unable to edit sesh. Please try again");
  };
  const editSesh = useEditMutation(api.seshes);
  const handleEditSesh = useMutationHandler(
    ["seshes"],
    editSesh,
    editSuccess,
    editError
  );

  // prefetch spots query to ensure its loaded by the time create sesh form needs it
  usePrefetchSpots();

  return (
    <>
      <Row id="feed">
        <Col>
          <Row>
            <Col xs={12} md={3} className="mx-auto">
              <Button
                className="w-100 my-2"
                color="primary"
                onClick={() => {
                  setEditMode(false);
                  toggleModal();
                }}
              >
                Add a sesh
              </Button>
            </Col>
          </Row>
          {isLoading ? (
            <Row>
              <Spinner className="global-spinner" />
            </Row>
          ) : seshes?.pages ? (
            seshes?.pages.map((page, i) => (
              <React.Fragment key={i}>{renderPage(page, i)}</React.Fragment>
            ))
          ) : null}
          <Row id="load-more-row">
            {isFetchingNextPage ? (
              <Spinner className="mx-auto" />
            ) : (
              <Button
                onClick={() => fetchNextPage()}
                color="primary"
                disabled={!hasNextPage || isFetchingNextPage}
                className="mx-auto"
              >
                {hasNextPage ? "Load More" : "All Seshes Loaded"}
              </Button>
            )}
          </Row>
        </Col>
      </Row>
      <ConfirmModal
        title={`Delete this Sesh?`}
        color="danger"
        message="This will permanently delete this sesh"
        onSubmit={() => confirmClick(seshToEdit)}
        toggle={toggleConfirm}
        isOpen={showConfirm}
      />
      <Modal
        id="edit-modal"
        className="modal-fullscreen"
        isOpen={modal}
        toggle={toggleModal}
        modalTransition={{ timeout: 50 }}
      >
        <Card body className="form-card">
          <NewSeshForm
            handleSubmit={editMode ? handleEditSesh : handleCreateSesh}
            handleCancel={toggleModal}
            handleDelete={handleDelete}
            seshToEdit={seshToEdit}
            editMode={editMode}
          />
        </Card>
      </Modal>
    </>
  );
};

Home.propTypes = {
  api: PropTypes.shape({
    seshes: PropTypes.shape({
      getOne: PropTypes.func.isRequired,
      getAll: PropTypes.func.isRequired,
      getPaged: PropTypes.func.isRequired,
      update: PropTypes.func.isRequired,
      create: PropTypes.func.isRequired,
      delete: PropTypes.func.isRequired,
    }),
  }),
};

export default Home;
